import { cnaesService, type Cnae } from '@/services';
import { useQuery } from '@tanstack/react-query';

/**
 * Busca SERVER-SIDE de CNAEs (DataTable `CNAE` do InsureMO) por **código ou
 * descrição** — o back roteia o filtro conforme o termo. A DataTable é grande
 * (catálogo CNAE completo), então não carregamos tudo no browser: filtramos no
 * servidor. O termo já chega debounced do caller.
 *
 * Termo vazio carrega uma página inicial; 1 caractere não dispara (ruído);
 * >= 2 busca.
 */
const INITIAL_PAGE_SIZE = 50;

export function useCnaeSearch(term: string | undefined) {
  const q = (term ?? '').trim();
  return useQuery<Cnae[]>({
    queryKey: ['products', 'cnaes', 'search', q.toLowerCase()],
    queryFn: async () => {
      const response = await cnaesService.getCnaes({
        filter: q || undefined,
        pageSize: INITIAL_PAGE_SIZE,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error ?? 'Erro ao consultar CNAEs');
      }
      return response.data.data;
    },
    enabled: q.length === 0 || q.length >= 2,
    retry: false,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Resolve UM código de CNAE já salvo no seu item completo (com descrição) —
 * usado para exibir o label quando o valor selecionado não está na busca atual
 * (ex.: hidratação de uma cotação existente).
 */
export function useCnaeLabel(code: string | undefined) {
  const c = (code ?? '').trim();
  return useQuery<Cnae | null>({
    queryKey: ['products', 'cnaes', 'by-code', c],
    queryFn: async () => {
      const response = await cnaesService.getCnaes({ filter: c, pageSize: 20 });
      if (!response.success || !response.data) return null;
      const items = response.data.data;
      return items.find(i => String(i.code) === c) ?? items[0] ?? null;
    },
    enabled: c.length >= 2,
    retry: false,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
